import React, { useRef } from 'react';
import TruncateMarkup from 'react-truncate-markup';
import { useSelections } from 'ahooks';
import { useParams } from 'react-router-dom';
import _ from 'lodash';

import { List, Radio } from 'src/components/custom';
import { RightFilled } from 'src/components/icons';
import { PATH } from 'src/constants/app';
import Item, { Props as ItemProps } from './Item';
import SubMenu, { Props as SubMenuProps } from './SubMenu';
import { MenuContext, MenuProps } from './context';
import useScrollIntoItem from 'src/components/coreUI/ClassSider/useScrollIntoItem';

export interface IMenu {
  (props: React.PropsWithChildren<MenuProps>): React.ReactElement;
  Item: React.FC<ItemProps>;
  SubMenu: React.FC<SubMenuProps>;
}

const Menu: IMenu = (props) => {
  const {
    menu = [],
    activeKeys = [],
    openKeys = [],
    children,
    itemProps,
    ...rest
  } = props;

  const openRef = useRef<(number | string)[]>(openKeys);
  const {
    selected: openSubmenus,
    toggle,
    setSelected,
  } = useSelections<number | string>(openKeys, openKeys);
  const { class_id, moduleId } =
    useParams<{ class_id: string; moduleId: string }>();

  if (!_.isEqual(openRef.current, openKeys)) {
    openRef.current = openKeys;
    setSelected(openKeys);
  }

  // React deps change warning so I can not pass openRef.current
  useScrollIntoItem(openRef.current?.[0]);

  return (
    <MenuContext.Provider
      value={{
        activeKeys: [...activeKeys],
        openKeys: openSubmenus,
        ...rest,
      }}
    >
      <List
        columns={1}
        className="ui menu vertical custom clear-space"
        data={menu}
        renderItem={(d, i) => {
          if (d.children) {
            return (
              <SubMenu
                className="mb-16"
                subMenuKey={d.key}
                icon={null}
                title={{
                  text: (
                    <Radio
                      wrapperClassName="justify-between"
                      value={d.key}
                      label={d.content}
                      id={d.key}
                      checked={d.completed}
                      extra={
                        <div className="ml-12 d-flex">
                          <span>{`(${d.no_contents_completed}/${d.children.length})`}</span>
                          <RightFilled className="mt-4 ml-4 right_filled" />
                        </div>
                      }
                      onClick={() => {
                        toggle(d.key);
                      }}
                    />
                  ),
                }}
                content={{
                  text: (
                    <Radio.Group
                      className="clear-space_vertical"
                      lineConnect
                      options={d.children.map((opt) => ({
                        value: opt.key,
                        label: opt.content,
                        completed: opt.completed,
                      }))}
                      renderItem={(option) => (
                        <Item
                          className="module_item_sider"
                          itemKey={option.value}
                          id={option.value}
                          to={`/${PATH.CLASS}/${class_id}/${option.value}`}
                          isActive={String(option.value) === moduleId}
                        >
                          <Radio
                            align="center"
                            key={option.value}
                            value={option.value}
                            checked={option.completed}
                            extra={
                              <TruncateMarkup>
                                <div className="ml-8">{option.label}</div>
                              </TruncateMarkup>
                            }
                          />
                        </Item>
                      )}
                    />
                  ),
                }}
              />
            );
          }

          return (
            <Item
              key={d.key}
              itemKey={d.key!}
              to={d.to}
              className={[itemProps?.className, d.className]
                .filter(Boolean)
                .join(' ')}
            >
              {d.content}
            </Item>
          );
        }}
      >
        {children}
      </List>
    </MenuContext.Provider>
  );
};

Menu.Item = Item;
Menu.SubMenu = SubMenu;

export default Menu;
